import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { getRenderable, isFunction } from '../lib';
import Toggler from '@fiverr/ui_toggle';
import { uid } from '@fiverr-private/futile/lib/random';
import sassVars from './index.scss';

const SHOWN_CLASS = 'shown';
const ESCAPE_KEY_CODE = 27;
const ESCAPE_KEY_NAME = 'Escape';

class Modal extends Toggler {

    constructor(props) {
        super(props);
        this.__view = this.__view.bind(this);
        this.draw = this.draw.bind(this);
        this.onEscape = this.onEscape.bind(this);
        this.openClass = `modal-open-${uid()}`;
    }

    componentDidMount() {
        if (this.props.autoOpen) { this.open(); }
    }

    componentDidUpdate() {
        this.draw();
    }

    componentWillUnmount() {
        super.componentWillUnmount();
        this.close();
    }

    onEscape(e) {
        if (e.keyCode === ESCAPE_KEY_CODE || e.which === ESCAPE_KEY_CODE || e.code === ESCAPE_KEY_NAME || e.key === ESCAPE_KEY_NAME) {
            this.close();
        }
    }

    get targetClassName() {
        return classnames(`MODAL__modal-${sassVars.uid}`, this.props.name);
    }

    get modalProps() {
        const {
            open, close, toggle, preventCloseAction
        } = this;

        const {
            isOpen
        } = this.state;

        const {
            trigger,
            children,
            ...props
        } = this.props;

        const modalProps = {
            open,
            close,
            toggle,
            preventCloseAction,
            isOpen,
            ...props
        };

        return modalProps;
    }

    togglerWillOpen() {
        if (this.target) { return; }

        this.target = document.createElement('div');
        this.target.className = this.targetClassName;

        document.body.appendChild(this.target);

        if (this.props.closeOnEscape) {
            window.addEventListener('keyup', this.onEscape);
        }

        this.draw();
    }

    togglerDidOpen() {
        document.body.classList.add(this.openClass);

        setTimeout(() => this.target.classList.add(SHOWN_CLASS));

        if (isFunction(this.props.onOpen)) {
            this.props.onOpen();
        }
    }

    togglerWillClose(done) {
        if (!this.target) { return; }

        this.target.classList.remove(SHOWN_CLASS);
        this.target.addEventListener('transitionend', () => {
            ReactDOM.unmountComponentAtNode(this.target);
            document.body.removeChild(this.target);
            this.target = undefined;
            done();
        }, { once: true });
        window.removeEventListener('keyup', this.onEscape);
    }

    togglerDidClose() {
        document.body.classList.remove(this.openClass);

        if (isFunction(this.props.onClose)) {
            this.props.onClose();
        }
    }

    __view(props) {
        return this.props.children(props);
    }

    draw() {
        if (!this.target) { return; }

        ReactDOM.unstable_renderSubtreeIntoContainer(
            this,
            this.__view(this.modalProps),
            this.target
        );
    }

    render() {
        return getRenderable(this.props.trigger, this.modalProps);
    }
}

Modal.propTypes = {
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    closeOnEscape: PropTypes.bool,
    bindBodyClickListener: PropTypes.bool,
    trigger: PropTypes.func,
    name: PropTypes.string
};

Modal.defaultProps = {
    closeOnEscape: true,
    bindBodyClickListener: true
};

export default Modal;
